import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Icon, useToast } from '@medely/ui-kit';
import { useProNavigate } from '@mp/contexts/NavigationContext/NavigationContext';
import { usePoolJobCta } from '@mp/hooks/usePoolJobCta';
import { useVerifyJobAvailable } from '@mp/hooks/useVerifyJobAvailable';
import { poolJobCtaLabel, type PoolJobTapAction } from '@mp/hooks/poolJobCta';
import type { PoolJobButtonJob, PoolJobButtonProps } from './interface';

const DEFAULT_TEST_ID = 'pool-job-button';

const SHIFT_UNAVAILABLE_MESSAGE = 'This shift is no longer available.';

const locationOnboardingParams = (job: PoolJobButtonJob) => ({
  poolId: job.pool_id as number,
});

export const PoolJobButton = ({
  job,
  children,
  onBook,
  onJoin,
  onAccept,
  onNavigateInterestSubmitted,
  testId = DEFAULT_TEST_ID,
}: PoolJobButtonProps): React.ReactElement | null => {
  const { navigate } = useProNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { cta, action, isLoading } = usePoolJobCta(job);
  const { verifyJobAvailable, verifyJobCanBook } = useVerifyJobAvailable();
  const [isActing, setIsActing] = useState(false);

  const poolId = job?.pool_id ?? null;
  const isPoolJob = poolId !== null && poolId !== undefined;

  const runSeam = async (seam?: (poolId: number) => void | Promise<unknown>) => {
    if (!seam || poolId === null) {
      return;
    }
    setIsActing(true);
    try {
      await seam(poolId);
    } finally {
      setIsActing(false);
    }
  };

  const goToOnboarding = async () => {
    const jobId = job?.id ?? null;
    if (jobId !== null) {
      setIsActing(true);
      let available: boolean;
      try {
        available = await verifyJobAvailable(jobId);
      } finally {
        setIsActing(false);
      }
      if (!available) {
        toast.error(SHIFT_UNAVAILABLE_MESSAGE);
        queryClient.removeQueries(['jobs']);
        navigate('Today');
        return;
      }
    }
    navigate('LocationOnboarding', locationOnboardingParams(job));
  };

  const goToBook = async () => {
    const jobId = job?.id ?? null;
    if (jobId === null) {
      return;
    }
    setIsActing(true);
    let canBook: boolean;
    try {
      canBook = await verifyJobCanBook(jobId);
    } finally {
      setIsActing(false);
    }

    if (canBook) {
      onBook?.();
    }
  };

  const handlers: Record<PoolJobTapAction, () => void | Promise<void>> = {
    none: () => undefined,
    book: () => onBook?.(),
    navigateMissingReqs: () => navigate('MissingAndExpiringConditions'),
    navigateOnboarding: goToOnboarding,
    navigateReview: () => onNavigateInterestSubmitted?.(),
    acceptThenOnboarding: async () => {
      await runSeam(onAccept);
      await goToOnboarding();
    },
    joinThenOnboarding: async () => {
      await runSeam(onJoin);
      await goToOnboarding();
    },
    joinThenReview: async () => {
      await runSeam(onJoin);
      onNavigateInterestSubmitted?.();
    },
    joinThenBook: async () => {
      await runSeam(onJoin);
      await goToBook();
    },
  };

  if (isPoolJob && isLoading) {
    return (
      <Button fullWidth disabled loading testId={testId} onClick={() => undefined}>
        {poolJobCtaLabel(cta) ?? 'Unlock'}
      </Button>
    );
  }

  if (cta === null || action === 'book') {
    return <>{children}</>;
  }

  const busy = isLoading || isActing;

  return (
    <Button
      fullWidth
      testId={testId}
      loading={busy}
      disabled={busy}
      startIcon={
        ['unlock_onboarding_review', 'unlock_missing_reqs', 'unlock_onboarding'].includes(cta) ? (
          <Icon name="lock" variant="custom" color="white" iconSize="16px" />
        ) : (
          <></>
        )
      }
      onClick={() => {
        void handlers[action]();
      }}
    >
      {poolJobCtaLabel(cta)}
    </Button>
  );
};

export default PoolJobButton;
